/**
 * Mutation helpers.
 *
 * One wrapper around TanStack's `useMutation` that does the three things every form in this app
 * needs and would otherwise repeat: surface the server's message, surface its per-field messages,
 * and invalidate the queries the change affected.
 */

import { useMutation, useQueryClient, type UseMutationOptions } from "@tanstack/react-query";
import { ApiError } from "./api";

export interface ApiMutationOptions<TInput, TOutput> {
  mutationFn: (input: TInput) => Promise<TOutput>;
  /** Query keys to refetch on success. `["*"]` clears everything, used after switching agency. */
  invalidates?: string[][];
  onSuccess?: (data: TOutput, input: TInput) => void | Promise<void>;
  options?: Omit<UseMutationOptions<TOutput, unknown, TInput>, "mutationFn" | "onSuccess">;
}

export function useApiMutation<TInput, TOutput>({
  mutationFn,
  invalidates,
  onSuccess,
  options,
}: ApiMutationOptions<TInput, TOutput>) {
  const queryClient = useQueryClient();

  const mutation = useMutation<TOutput, unknown, TInput>({
    ...options,
    mutationFn,
    onSuccess: async (data, input) => {
      for (const key of invalidates ?? []) {
        if (key[0] === "*") await queryClient.invalidateQueries();
        else await queryClient.invalidateQueries({ queryKey: key });
      }
      await onSuccess?.(data, input);
    },
  });

  const error = mutation.error;
  return {
    ...mutation,
    /** The message to show at the top of the form. */
    errorMessage: error ? (error instanceof Error ? error.message : "Something went wrong.") : null,
    /** Per-field messages, keyed by input name. */
    fieldErrors: error instanceof ApiError ? (error.fieldErrors ?? {}) : {},
  };
}

/**
 * Read a form's values as a plain object.
 *
 * Inputs stay uncontrolled — the browser keeps their values, so nothing is lost when the server
 * rejects a submission, and the form still behaves sensibly before React has hydrated.
 */
export function formValues(form: HTMLFormElement): Record<string, string> {
  const data = new FormData(form);
  const out: Record<string, string> = {};
  for (const [key, value] of data.entries()) {
    if (typeof value === "string") out[key] = value.trim();
  }
  return out;
}

/** Checkbox groups (`name="roles"` repeated) need every value, not just the last. */
export function formValueList(form: HTMLFormElement, name: string): string[] {
  return new FormData(form)
    .getAll(name)
    .filter((value): value is string => typeof value === "string");
}

export function formChecked(form: HTMLFormElement, name: string): boolean {
  const field = form.elements.namedItem(name);
  return field instanceof HTMLInputElement ? field.checked : false;
}

/** Empty strings become null, so "not set" and "set to nothing" stay distinguishable. */
export function nullIfBlank(value: string | undefined): string | null {
  return value && value.trim() !== "" ? value.trim() : null;
}
